/**
 * Text Helpers
 * 
 * Utility functions for text processing and rendering.
 * Used across v2 for consistent text display.
 */

/**
 * Decode HTML entities to their actual characters
 * Handles common entities returned from APIs
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';
  
  // Common HTML entities map
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&#x2F;': '/',
    '&#47;': '/',
    '&#x27;': "'",
    '&nbsp;': ' ',
    '&#x3A;': ':',
    '&#58;': ':',
    '&#x2C;': ',',
    '&#44;': ',',
    '&#x2E;': '.',
    '&#46;': '.',
    '&#x40;': '@',
    '&#64;': '@',
    '&#x23;': '#',
    '&#35;': '#',
    '&#x24;': '$',
    '&#36;': '$',
    '&#x25;': '%',
    '&#37;': '%',
    '&#x26;': '&',
    '&#38;': '&',
    '&#x2B;': '+',
    '&#43;': '+',
    '&#x3D;': '=',
    '&#61;': '=',
    '&#x3F;': '?',
    '&#63;': '?',
    '&#x21;': '!',
    '&#33;': '!',
  };

  let decoded = text;
  
  // Replace named and numeric entities
  for (const [entity, char] of Object.entries(entities)) {
    decoded = decoded.split(entity).join(char);
  }
  
  // Handle remaining numeric entities (decimal)
  decoded = decoded.replace(/&#(\d+);/g, (_, code) => 
    String.fromCharCode(parseInt(code, 10))
  );
  
  // Handle remaining hex entities
  decoded = decoded.replace(/&#x([0-9A-Fa-f]+);/g, (_, code) => 
    String.fromCharCode(parseInt(code, 16))
  );
  
  return decoded;
}

/**
 * Sanitize and decode text for safe display
 * Decodes HTML entities while keeping the text safe
 */
export function sanitizeDisplayText(text: string | null | undefined): string {
  if (!text) return '';
  return decodeHtmlEntities(text);
}

/**
 * Format user-generated content for display
 * Decodes entities and trims whitespace
 */
export function formatUserContent(content: string | null | undefined): string {
  if (!content) return '';
  return decodeHtmlEntities(content.trim());
}

